import {useEffect, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import Layout, {Caricamento, Titolo, Vuoto} from '../Layout';
import {handleApiError} from '../api';
import {
    BottoniDownload, Errore, FlagInviata, StatoOrdine, TipoDocumento, euro, gestisciErrore, getFattura, getFatture,
    oggi, primoDelMese, rifDaDocumento, scaricaZipFatture, soloData,
} from '../woo';

// Fatture e note di credito generate dal plugin POP sul sito: elenco per periodo, download
// XML/PDF (singoli o zip) e flag "inviata" da impostare dopo la trasmissione allo SdI.
export default function FatturePage() {
    const [params, setParams] = useSearchParams();
    const filtri = {
        dal: params.get('dal') ?? primoDelMese(),
        al: params.get('al') ?? oggi(),
        tipo: params.get('tipo') ?? '',
        stato: params.get('stato') ?? '',
    };
    const [form, setForm] = useState(filtri);
    const [risultato, setRisultato] = useState(null);
    const [dettagli, setDettagli] = useState({});   // chiave documento -> dati da /woo/fatture/:id
    const [errore, setErrore] = useState('');
    const [conPdf, setConPdf] = useState(false);
    const [zipInCorso, setZipInCorso] = useState(false);
    const richiesta = useRef(0); // per ignorare i caricamenti di una ricerca precedente

    useEffect(() => {
        const n = ++richiesta.current;
        setRisultato(null);
        setDettagli({});
        setErrore('');
        getFatture({from: filtri.dal, to: filtri.al, tipo: filtri.tipo, stato: filtri.stato})
            .then((r) => {
                if (n !== richiesta.current) return;
                setRisultato(r);
                caricaDettagli(r.documenti, n);
            })
            .catch(gestisciErrore(setErrore));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtri.dal, filtri.al, filtri.tipo, filtri.stato]);

    // La ricerca non dice se il documento e' gia' stato inviato: lo chiediamo documento per
    // documento, poche richieste alla volta per non caricare il sito.
    function caricaDettagli(documenti, n) {
        const coda = [...documenti];
        const lavoratore = async () => {
            while (coda.length > 0 && n === richiesta.current) {
                const d = coda.shift();
                const rif = rifDaDocumento(d);
                let dati;
                try {
                    dati = await getFattura(rif);
                } catch (err) {
                    if (err.status === 401) return handleApiError(err);
                    dati = {errore: err.message};
                }
                if (n === richiesta.current) setDettagli((prev) => ({...prev, [chiave(d)]: dati}));
            }
        };
        Promise.all([lavoratore(), lavoratore(), lavoratore()]);
    }

    function cerca(event) {
        event.preventDefault();
        setParams(form);
    }

    function scaricaZip() {
        setZipInCorso(true);
        scaricaZipFatture({from: filtri.dal, to: filtri.al, tipo: filtri.tipo, stato: filtri.stato, pdf: conPdf ? 'true' : undefined})
            .catch((err) => err.status === 401 ? handleApiError(err) : alert(err.message))
            .finally(() => setZipInCorso(false));
    }

    const documenti = risultato?.documenti ?? [];
    const daInviare = documenti.filter((d) => dettagli[chiave(d)]?.invoice_sent === 'no_sent').length;

    return (
        <Layout>
            <Titolo>Fatture e-commerce</Titolo>
            <div className="card mb-3">
                <div className="card-body">
                    <form className="row g-3 align-items-end" onSubmit={cerca}>
                        <div className="col-auto">
                            <label className="form-label" htmlFor="dal">Dal</label>
                            <input id="dal" type="date" className="form-control" value={form.dal} required
                                   onChange={(e) => setForm({...form, dal: e.target.value})}/>
                        </div>
                        <div className="col-auto">
                            <label className="form-label" htmlFor="al">Al</label>
                            <input id="al" type="date" className="form-control" value={form.al} required
                                   onChange={(e) => setForm({...form, al: e.target.value})}/>
                        </div>
                        <div className="col-auto">
                            <label className="form-label" htmlFor="tipo">Tipo</label>
                            <select id="tipo" className="form-select" value={form.tipo}
                                    onChange={(e) => setForm({...form, tipo: e.target.value})}>
                                <option value="">Tutti</option>
                                <option value="fattura">Fatture</option>
                                <option value="nota_credito">Note di credito</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <label className="form-label" htmlFor="stato">Stato ordine</label>
                            <select id="stato" className="form-select" value={form.stato}
                                    onChange={(e) => setForm({...form, stato: e.target.value})}>
                                <option value="">Tutti</option>
                                <option value="completed">Completato</option>
                                <option value="processing">In lavorazione</option>
                                <option value="refunded">Rimborsato</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <button type="submit" className="btn btn-primary">
                                <i className="bi-search me-2"></i>Cerca
                            </button>
                        </div>
                        <div className="col-12 text-secondary small">
                            Il periodo si riferisce alla data dell'ordine (o del rimborso per le note di credito), non alla data della fattura.
                        </div>
                    </form>
                </div>
            </div>

            <Errore messaggio={errore}/>
            <div className="card">
                <div className="card-body">
                    {!errore && risultato === null && <Caricamento/>}
                    {risultato && documenti.length === 0 && <Vuoto>Nessun documento nel periodo.</Vuoto>}
                    {documenti.length > 0 && (
                        <>
                            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                                <span className="text-secondary small">
                                    {risultato.fatture} fatture, {risultato.note_credito} note di credito
                                    {daInviare > 0 && <>, <strong>{daInviare} da inviare</strong></>}
                                </span>
                                <div className="ms-auto d-flex align-items-center gap-3">
                                    <div className="form-check mb-0">
                                        <input id="pdf" type="checkbox" className="form-check-input" checked={conPdf}
                                               onChange={(e) => setConPdf(e.target.checked)}/>
                                        <label className="form-check-label" htmlFor="pdf">anche i PDF</label>
                                    </div>
                                    <button type="button" className="btn btn-primary" onClick={scaricaZip} disabled={zipInCorso}>
                                        {zipInCorso
                                            ? <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            : <i className="bi-file-zip me-2"></i>}
                                        Scarica zip XML del periodo
                                    </button>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead>
                                    <tr>
                                        <th>Numero</th>
                                        <th>Tipo</th>
                                        <th>Data</th>
                                        <th>Ordine</th>
                                        <th>Stato ordine</th>
                                        <th className="text-end">Totale</th>
                                        <th>Invio SdI</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {documenti.map((d) => {
                                        const rif = rifDaDocumento(d);
                                        const k = chiave(d);
                                        const ordine = d.document_type === 'credit_note' && d.parent_id ? d.parent_id : d.document_id;
                                        return (
                                            <tr key={k}>
                                                <td className="fw-semibold">{d.invoice_number || <span className="text-secondary">—</span>}</td>
                                                <td><TipoDocumento tipo={d.document_type}/></td>
                                                <td className="text-nowrap">{soloData(d.date_created)}</td>
                                                <td>
                                                    <Link to={`/ordini/${ordine}`}>#{ordine}</Link>
                                                    {d.document_type === 'credit_note' && <span className="text-secondary small ms-1">rimborso {d.document_id}</span>}
                                                </td>
                                                <td><StatoOrdine stato={d.status}/></td>
                                                <td className="text-end text-nowrap">{euro(d.total)}</td>
                                                <td>
                                                    <FlagInviata rif={rif} documento={dettagli[k]} piccoli
                                                                 onAggiornato={(doc) => setDettagli((prev) => ({...prev, [k]: doc}))}/>
                                                </td>
                                                <td className="text-end"><BottoniDownload rif={rif} piccoli/></td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function chiave(d) {
    return `${d.document_type}-${d.document_id}`;
}
